import React from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { FileText, Calendar, User } from "lucide-react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import { getCustomerDisplayName } from "@/utils/customerDisplayHelpers";
import EstimateStatusBadge from "./components/EstimateStatusBadge";
import EstimateActions from "./components/EstimateActions";

type EstimateStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled';

interface Estimate {
  id: string;
  estimate_number?: string | null;
  title: string;
  status: string;
  version: number;
  total_amount?: number | null;
  created_at: string;
  expires_at?: string | null;
  customers?: {
    id: string;
    name: string;
    last_name: string;
    business_name?: string | null;
    customer_type?: string;
  } | null;
}

interface EstimateCardProps {
  estimates: Estimate[];
}

/**
 * EstimateCard - Card/grid view for estimates list
 * 
 * Displays estimates as visual cards showing:
 * - Estimate number and title
 * - Status badge
 * - Customer name
 * - Total amount
 * - Created and expiry dates
 * - Actions menu
 */
const EstimateCard: React.FC<EstimateCardProps> = ({ estimates }) => {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {estimates.map((estimate) => (
        <Card key={estimate.id} className="hover:shadow-md transition-shadow overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                <Link 
                  to={`/estimates/${estimate.id}`} 
                  className="font-semibold font-montserrat hover:underline truncate"
                >
                  {estimate.title}
                </Link>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground font-mono truncate">
                    {estimate.estimate_number || 'N/A'}
                  </span>
                  <EstimateStatusBadge 
                    status={estimate.status as EstimateStatus} 
                    expiresAt={estimate.expires_at}
                    size="sm"
                  />
                </div>
                <EstimateActions
                  estimateId={estimate.id}
                  estimateTitle={estimate.title}
                  estimateStatus={estimate.status}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Customer */}
            {estimate.customers && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <User className="h-4 w-4" />
                <span>{getCustomerDisplayName(estimate.customers)}</span>
              </div>
            )}
            
            {/* Amount */}
            <div className="text-lg font-semibold mb-3">
              {formatCurrency(estimate.total_amount || 0)}
            </div>
            
            {/* Dates */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Created: {format(new Date(estimate.created_at), 'dd MMMM yyyy')}
              </div>
              {estimate.expires_at && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span className={
                    new Date(estimate.expires_at) < new Date() 
                      ? 'text-destructive font-medium' 
                      : ''
                  }>
                    Expires: {format(new Date(estimate.expires_at), 'dd MMMM yyyy')}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-2">
              <span className="text-xs text-muted-foreground">v{estimate.version}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default EstimateCard;
